import { YouTubePlayer as IYouTubePlayer, Options as IYouTubePlayerOptions } from 'youtube-player/dist/types';
import PlayerState from 'youtube-player/dist/constants/PlayerStates';
import getComponentInstanceFor from '@swey/vue-hydration/getComponentInstanceFor';
import UserConsentUtil, { UserConsentEvent } from '../../../utils/UserConsentUtil';

export default class VideoComponent {
	private static domSelectors = {
		wrapper: '.c-video-component',
		videoWrapper: '.c-video-component__player',
		playButton: '.c-video-component__play-button',
		closeButton: '.c-video-component__close-button',
		placeholderImage: '.c-video-component__image',
		consentPromptButton: '.c-video-component__consent-prompt-button'
	};

	private static stateClasses = {
		isOpen: 'is-open',
		isPlaying: 'is-playing',
		isPaused: 'is-paused',
		missingUserConsent: 'missing-user-consent'
	};

	public static events = {
		VIDEO_OPENED: 'VideoComponent.opened',
		VIDEO_CLOSED: 'VideoComponent.closed',
		VIDEO_ENDED: 'VideoComponent.ended',
		VIDEO_PAUSED: 'VideoComponent.paused',
		VIDEO_PLAYING: 'VideoComponent.playing'
	};

	private readonly element: HTMLElement;

	public player?: IYouTubePlayer;

	public constructor(element: HTMLElement) {
		this.element = element;
		this.init();
	}

	public closeVideo(): void {
		if (!this.player) return;
		this.element.classList.remove(VideoComponent.stateClasses.isOpen);
		this.player.stopVideo();
		this.element.dispatchEvent(new CustomEvent(VideoComponent.events.VIDEO_CLOSED));
	}

	private init(): void {
		this.initEvents();
	}

	private async initVideoPlayer(): Promise<void> {
		if (!UserConsentUtil.marketingConsent || this.player) return;

		const videoWrapper = this.element.querySelector(VideoComponent.domSelectors.videoWrapper) as HTMLElement;
		const options = JSON.parse(this.element.getAttribute('data-options') as string) as IYouTubePlayerOptions;

		const optionsWithDefaults: IYouTubePlayerOptions = {
			height: 405,
			width: 720,
			...options,
			playerVars: {
				enablejsapi: 1,
				modestbranding: 1,
				controls: 1,
				// eslint-disable-next-line @typescript-eslint/camelcase
				iv_load_policy: 3,
				fs: 0,
				playsinline: 1,
				rel: 0,
				...options.playerVars
			}
		};

		const initYouTubePlayer: any = (await import('youtube-player')).default;

		this.player = initYouTubePlayer(videoWrapper, optionsWithDefaults) as IYouTubePlayer;

		this.player.on('stateChange', (event): void => {
			if (event.data === PlayerState.ENDED) {
				this.closeVideo();
				this.element.dispatchEvent(new CustomEvent(VideoComponent.events.VIDEO_ENDED));
			}
		});

		this.player.on('stateChange', (event): void => {
			if (event.data === PlayerState.PAUSED) {
				this.element.classList.remove(VideoComponent.stateClasses.isPlaying);
				this.element.classList.add(VideoComponent.stateClasses.isPaused);
				this.element.dispatchEvent(new CustomEvent(VideoComponent.events.VIDEO_PAUSED));
			}
		});

		this.player.on('stateChange', (event): void => {
			if (event.data === PlayerState.PLAYING) {
				this.element.classList.remove(VideoComponent.stateClasses.isPaused);
				this.element.classList.add(VideoComponent.stateClasses.isPlaying);
				this.element.dispatchEvent(new CustomEvent(VideoComponent.events.VIDEO_PLAYING));
			}
		});
	}

	private initEvents(): void {
		const playButton = this.element.querySelector(VideoComponent.domSelectors.playButton);
		const placeholderImage = this.element.querySelector(VideoComponent.domSelectors.placeholderImage);
		const closeButton = this.element.querySelector(VideoComponent.domSelectors.closeButton);
		const consentPromptButton = this.element.querySelector(VideoComponent.domSelectors.consentPromptButton);

		window.addEventListener(UserConsentEvent.ConsentUpdate, (): void => {
			if (UserConsentUtil.marketingConsent) {
				this.element.classList.remove(VideoComponent.stateClasses.missingUserConsent);
			}
		});

		if (playButton) {
			playButton.addEventListener('click', (): void => {
				this.openVideo();
			});
		}

		if (placeholderImage) {
			placeholderImage.addEventListener('click', (): void => {
				this.openVideo();
			});
		}

		if (closeButton) {
			closeButton.addEventListener('click', (): void => this.closeVideo());
		}

		if (consentPromptButton) {
			consentPromptButton.addEventListener('click', (): void => UserConsentUtil.requestConsent());
		}
	}

	public async openVideo(): Promise<void> {
		if (!UserConsentUtil.marketingConsent) {
			this.element.classList.add(VideoComponent.stateClasses.missingUserConsent);
			UserConsentUtil.requestConsent();
			return;
		}

		if (!this.player) {
			await this.initVideoPlayer();
		}

		this.closeAllVideosPlayingOnPage();
		this.element.classList.add(VideoComponent.stateClasses.isOpen);
		(this.player as IYouTubePlayer).playVideo();
		this.element.dispatchEvent(new CustomEvent(VideoComponent.events.VIDEO_OPENED));
	}

	private closeAllVideosPlayingOnPage(): void {
		const videoElements: HTMLElement[] = Array.from(document.querySelectorAll(VideoComponent.domSelectors.wrapper));

		videoElements.forEach((videoElement): void => {
			const video: VideoComponent = getComponentInstanceFor(videoElement) as VideoComponent;
			video.closeVideo();
		});
	}
}
